import React, { useEffect, useId, useRef, useState, useMemo } from 'react';

const DEFAULT_THINKING_STEPS = [
  { text: 'Trabalhando...', duration: 2000 },
  { text: 'Analisando pergunta...', duration: 2500 },
  { text: 'Buscando na documentação...', duration: 3000 },
  { text: 'Consultando fontes...', duration: 3500 },
  { text: 'Gerando resposta...', duration: null },
];

const TRANSITION_MS = 300;

function OryaThinkingIcon() {
  const clipId = useId().replace(/:/g, '');

  return (
    <svg
      className="aprender-orya-thinking-status__icon"
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden
    >
      <g clipPath={`url(#${clipId})`}>
        <path
          d="M9.74902 11V8.5M9.74902 3.5V1M10.999 2.25H8.49902M10.999 9.75H8.49902M5.49902 1.5L6.36611 3.75443C6.50712 4.12105 6.57762 4.30435 6.68726 4.45854C6.78443 4.5952 6.90382 4.71459 7.04048 4.81176C7.19467 4.9214 7.37798 4.99191 7.74459 5.13291L9.99902 6L7.74459 6.86709C7.37798 7.00809 7.19467 7.0786 7.04048 7.18823C6.90382 7.28541 6.78443 7.4048 6.68726 7.54146C6.57762 7.69565 6.50712 7.87895 6.36611 8.24557L5.49902 10.5L4.63193 8.24557C4.49093 7.87895 4.42043 7.69565 4.31079 7.54146C4.21362 7.4048 4.09422 7.28541 3.95757 7.18823C3.80338 7.0786 3.62007 7.00809 3.25345 6.86709L0.999023 6L3.25345 5.13291C3.62007 4.99191 3.80338 4.9214 3.95757 4.81176C4.09422 4.71459 4.21362 4.5952 4.31079 4.45854C4.42043 4.30435 4.49093 4.12105 4.63193 3.75443L5.49902 1.5Z"
          stroke="currentColor"
          strokeWidth="0.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect width="12" height="12" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

export default function OryaThinkingStatus({ active, steps }) {
  const thinkingSteps = useMemo(
    () => (steps?.length ? steps : DEFAULT_THINKING_STEPS),
    [steps]
  );
  const [stepIndex, setStepIndex] = useState(0);
  const [label, setLabel] = useState(thinkingSteps[0].text);
  const [phase, setPhase] = useState('enter');
  const timersRef = useRef([]);

  const clearTimers = () => {
    timersRef.current.forEach((id) => clearTimeout(id));
    timersRef.current = [];
  };

  const queueTimer = (fn, delay) => {
    const id = setTimeout(fn, delay);
    timersRef.current.push(id);
    return id;
  };

  useEffect(() => {
    if (!active) return undefined;

    clearTimers();
    setStepIndex(0);
    setLabel(thinkingSteps[0].text);
    setPhase('enter');

    const advanceFrom = (index) => {
      const step = thinkingSteps[index];
      if (!step?.duration || index >= thinkingSteps.length - 1) return;

      queueTimer(() => {
        setPhase('exit');
        queueTimer(() => {
          const nextIndex = index + 1;
          const nextStep = thinkingSteps[nextIndex];
          setStepIndex(nextIndex);
          setLabel(nextStep.text);
          setPhase('enter');
          advanceFrom(nextIndex);
        }, TRANSITION_MS);
      }, step.duration);
    };

    advanceFrom(0);

    return clearTimers;
  }, [active, thinkingSteps]);

  if (!active) return null;

  const labelClass = [
    'aprender-orya-thinking-status__label',
    phase === 'exit' ? 'aprender-orya-thinking-status__label--exit' : 'aprender-orya-thinking-status__label--enter',
  ].join(' ');

  return (
    <div className="aprender-orya-thinking-status" aria-live="polite">
      <div className="aprender-orya-thinking-status__header">
        <OryaThinkingIcon />
        <div className="aprender-orya-thinking-status__text-slot">
          <span className="aprender-orya-thinking-status__sizer">{label}</span>
          <span className={labelClass} key={`${stepIndex}-${label}`}>
            {label}
          </span>
        </div>
      </div>
    </div>
  );
}
